/**
 * <price-range-slider data-skip-subtree-update>
 *
 * Dual-handle range slider that sits on top of the native price min/max text
 * inputs (filter.v.price.gte / filter.v.price.lte). Price is the ONLY field
 * Shopify range-filters natively, so — unlike the diameter/lengte sliders, which
 * fake range by toggling discrete metafield checkboxes — this slider writes plain
 * numeric euro values straight into the existing inputs and calls the theme's own
 * price-facet-component.updatePriceFilterAndResults(). The text inputs stay in the
 * DOM (hidden via CSS once the slider is ready) and remain the single source of
 * truth, so all native behaviour (AJAX section render, summary, clear, history)
 * keeps working unchanged.
 *
 * Step size: €50 by default; €100 when the collection's highest price is high
 * (>= €5000), because €50 increments become meaninglessly fine and produce far
 * too many steps on high-value collections (gold/diamonds/watches). Both values
 * are the ones approved for this build.
 *
 * IMPORTANT: the element carries data-skip-subtree-update. The theme re-renders
 * collection sections through morph (assets/morph.js) on every filter change.
 * Without that attribute, morph reconciles this element against the empty server
 * markup and wipes the JS-built UI.
 *
 * Robustness (mirrors assets/diameter-slider.js):
 * - Never caches the text-input nodes' values; re-reads them each time.
 * - A MutationObserver on the price-facet-component watches the inputs' `value`
 *   attribute. morph rewrites that attribute after every section re-render
 *   (clear-all, another facet, back/forward), so we re-sync the handles from the
 *   authoritative server state. Our own apply only sets the `.value` property
 *   (never the attribute) -> it does not trigger the observer -> no feedback loop.
 * - Progressive enhancement: the native inputs are only hidden (via the
 *   `price-facet--slider-ready` class we add) once the slider has built
 *   successfully. If the JS fails or values are missing, the inputs stay visible.
 *
 * Self-contained on purpose: no imports (mirrors assets/diameter-slider.js), so a
 * module-resolution failure can never stop the component from registering.
 */

const STEP_HIGH_THRESHOLD = 5000; // euros: at/above this collection max, use €100 steps
const STEP_DEFAULT = 50;
const STEP_HIGH = 100;

/**
 * Normalize a (possibly localized) money string to a plain dot-decimal number
 * string. Replicates the theme's own formatMoney (assets/utilities.js) so we read
 * values exactly the way the native price inputs do.
 */
function parseMoney(value) {
  const s = String(value ?? '').replace(/\s/g, '');
  if (s.indexOf(',') === -1) return s; // "2500" or "2500.00"
  if (s.indexOf(',') < s.indexOf('.')) return s.replace(/,/g, ''); // "2,500.00"
  if (s.indexOf('.') < s.indexOf(',')) return s.replace(/\./g, '').replace(',', '.'); // "2.500,00"
  return s.replace(',', '.'); // "2500,00"
}

class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;

    // The slider is rendered as a sibling of price-facet-component inside the
    // panel, so its range-input change events do NOT hit the component's
    // capture-phase on:change delegation. Locate the component within the panel.
    const panel = this.closest('floating-panel-component') || this.parentElement;
    this.facet = panel ? panel.querySelector('price-facet-component') : null;
    if (!this.facet) return;

    this.minText = this.facet.querySelector('[ref="minInput"]');
    this.maxText = this.facet.querySelector('[ref="maxInput"]');
    if (!this.minText || !this.maxText) return;

    this.symbol = this.dataset.currencySymbol || '';
    this.lang = document.documentElement.lang || 'nl';

    this.rangeMax = this.#num(this.dataset.rangeMax);
    if (!(this.rangeMax > 0)) return;

    this.step = this.rangeMax >= STEP_HIGH_THRESHOLD ? STEP_HIGH : STEP_DEFAULT;
    // Round the top of the track up to a whole step so the max handle can always
    // reach (and cover) the real highest price.
    this.trackMax = Math.ceil(this.rangeMax / this.step) * this.step;

    this.#build();
    this._ready = true;

    // Hide the native inputs only now that the slider is actually in place.
    this.facet.classList.add('price-facet--slider-ready');

    // Re-sync whenever morph rewrites the text inputs' `value` attribute
    // (clear-all, removing another facet, browser back/forward). morph sets the
    // attribute itself (assets/morph.js: updateInput), so this fires AFTER the
    // section re-render with the authoritative state.
    this._observer = new MutationObserver(() => this.#resync());
    this._observer.observe(this.facet, { subtree: true, attributes: true, attributeFilter: ['value'] });
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  /** Parse a money string (localized or plain) to a number of euros; 0 on failure. */
  #num(value) {
    const n = Number(parseMoney(value));
    return Number.isFinite(n) ? n : 0;
  }

  /** Snap an euro value to the step grid, clamped to [0, trackMax]. */
  #snap(v) {
    return Math.min(this.trackMax, Math.max(0, Math.round(v / this.step) * this.step));
  }

  /** Current min from the text input: empty = 0 (no lower bound). */
  #readMin() {
    return this.minText.value.trim() ? this.#snap(this.#num(this.minText.value)) : 0;
  }

  /** Current max from the text input: empty = trackMax (no upper bound). */
  #readMax() {
    return this.maxText.value.trim() ? this.#snap(this.#num(this.maxText.value)) : this.trackMax;
  }

  /** Format an euro amount for display, e.g. "€2.500". */
  #fmt(v) {
    let amount;
    try {
      amount = new Intl.NumberFormat(this.lang, { maximumFractionDigits: 0 }).format(v);
    } catch {
      amount = String(v);
    }
    return `${this.symbol}${amount}`;
  }

  #build() {
    this.lo = this.#readMin();
    this.hi = this.#readMax();
    if (this.lo > this.hi) this.lo = this.hi;

    this.innerHTML = `
      <div class="price-slider__values">
        <span class="price-slider__value price-slider__value--min"></span>
        <span class="price-slider__sep">–</span>
        <span class="price-slider__value price-slider__value--max"></span>
      </div>
      <div class="price-slider__track">
        <div class="price-slider__rail"></div>
        <div class="price-slider__fill"></div>
        <input class="price-slider__range price-slider__range--min" type="range"
               min="0" max="${this.trackMax}" step="${this.step}" value="${this.lo}" aria-label="Minimumprijs">
        <input class="price-slider__range price-slider__range--max" type="range"
               min="0" max="${this.trackMax}" step="${this.step}" value="${this.hi}" aria-label="Maximumprijs">
      </div>`;

    this.minInput = this.querySelector('.price-slider__range--min');
    this.maxInput = this.querySelector('.price-slider__range--max');
    this.fill = this.querySelector('.price-slider__fill');
    this.minLabel = this.querySelector('.price-slider__value--min');
    this.maxLabel = this.querySelector('.price-slider__value--max');

    this.minInput.addEventListener('input', this.#onInput);
    this.maxInput.addEventListener('input', this.#onInput);
    // 'change' fires on pointer release / keyboard commit -> apply + run the filter.
    this.minInput.addEventListener('change', this.#onChange);
    this.maxInput.addEventListener('change', this.#onChange);

    this.#render();
  }

  /** Live update while dragging: clamp crossover, keep the active handle on top, redraw. */
  #onInput = (event) => {
    let lo = parseInt(this.minInput.value, 10);
    let hi = parseInt(this.maxInput.value, 10);

    if (lo > hi) {
      if (event.target === this.minInput) {
        lo = hi;
        this.minInput.value = String(lo);
      } else {
        hi = lo;
        this.maxInput.value = String(hi);
      }
    }

    this.lo = lo;
    this.hi = hi;

    this.minInput.style.zIndex = event.target === this.minInput ? '5' : '4';
    this.maxInput.style.zIndex = event.target === this.maxInput ? '5' : '4';

    this.#render();
  };

  #onChange = () => this.#apply();

  #render() {
    const span = this.trackMax || 1;
    const minPct = (this.lo / span) * 100;
    const maxPct = (this.hi / span) * 100;

    this.fill.style.insetInlineStart = `${minPct}%`;
    this.fill.style.insetInlineEnd = `${100 - maxPct}%`;

    this.minLabel.textContent = this.#fmt(this.lo);
    this.maxLabel.textContent = this.#fmt(this.hi);
  }

  /**
   * Write the selected range into the native inputs and run the native filter.
   * Empty bound = no filter on that side (full range = no price filter at all).
   */
  #apply() {
    const hasLower = this.lo > 0;
    const hasUpper = this.hi < this.trackMax;

    // Plain integer strings; server accepts filter.v.price.gte/lte in euros.
    this.minText.value = hasLower ? String(this.lo) : '';
    this.maxText.value = hasUpper ? String(this.hi) : '';

    if (typeof this.facet.updatePriceFilterAndResults === 'function') {
      this.facet.updatePriceFilterAndResults();
    } else {
      // Fallback: bubble a change to the component's on:change delegation.
      this.maxText.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** Re-sync handles from the current (server-authoritative) text-input values. */
  #resync() {
    if (!this.minInput) return;

    this.lo = this.#readMin();
    this.hi = this.#readMax();
    if (this.lo > this.hi) this.lo = this.hi;

    this.minInput.value = String(this.lo);
    this.maxInput.value = String(this.hi);
    this.#render();
  }
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
